import type { BoardData, Channel } from '../model/schema.js';
import { traceForward } from '../trace/trace.js';
import { fieldColumns } from './columns.js';
import { itemName } from '../trace/ports.js';
import { brandModel, byNumber, isPatchInput, portLabel } from './helpers.js';
import { standOf, standText } from '../stands/stands.js';

export interface InputListRow {
  rowKey: string;
  channelId: string;
  number: number;
  name: string;
  /** Source item name, else the informal `sourceText`. */
  source: string;
  model: string;
  phantom: boolean;
  /** Formal patch point (`SB1 In 5`), else the informal `Channel.patch`. */
  patch: string;
  /** True if `patch` comes from free text. */
  patchInformal: boolean;
  /** Sub-core point (U44): the cabled sub-core input, else the informal `Channel.subPatch`. */
  subPatch: string;
  subPatchInformal: boolean;
  group: string;
  /** Formal stand of the source (via `mountedOn`), else the informal `Channel.stand`. */
  stand: string;
  /** True if `stand` comes from free text. */
  standInformal: boolean;
  insert: string;
  notes: string;
}

function formalPatch(data: BoardData, ch: Channel): string | undefined {
  const sourceId = ch.sourceItemId;
  if (sourceId === undefined || data.items[sourceId] === undefined) return undefined;
  const portId = ch.sourcePortId;
  if (portId !== undefined && isPatchInput(data, { type: 'port', itemId: sourceId, portId })) {
    return portLabel(data, sourceId, portId);
  }
  const start = portId === undefined ? { itemId: sourceId } : { itemId: sourceId, portId };
  const hit = traceForward(data, start).find((h) => isPatchInput(data, h.to));
  return hit?.to.type === 'port' ? portLabel(data, hit.to.itemId, hit.to.portId) : undefined;
}

/** The sub-core input the source is cabled into, if any (U44). */
function formalSubPatch(data: BoardData, ch: Channel): string | undefined {
  const sourceId = ch.sourceItemId;
  if (sourceId === undefined || data.items[sourceId] === undefined) return undefined;
  const start =
    ch.sourcePortId === undefined
      ? { itemId: sourceId }
      : { itemId: sourceId, portId: ch.sourcePortId };
  const hit = traceForward(data, start).find(
    (h) => h.to.type === 'port' && data.items[h.to.itemId]?.kind === 'subcore',
  );
  return hit?.to.type === 'port' ? portLabel(data, hit.to.itemId, hit.to.portId) : undefined;
}

function needsPhantom(data: BoardData, ch: Channel): boolean {
  const item = ch.sourceItemId === undefined ? undefined : data.items[ch.sourceItemId];
  return (item?.ports ?? []).some((p) => p.direction === 'out' && p.phantom === true);
}

/**
 * Input list: one row per channel, ordered by channel number. Phantom is the channel's explicit
 * value, else whether the source needs +48V. The stand description is in `locale` (OIL-204).
 */
export function inputList(data: BoardData, locale: 'en' | 'de' = 'en'): InputListRow[] {
  return Object.values(data.channels)
    .map((ch): InputListRow => {
      const item = ch.sourceItemId === undefined ? undefined : data.items[ch.sourceItemId];
      const formal = formalPatch(data, ch);
      const formalSub = formalSubPatch(data, ch);
      const stand = standOf(data, item);
      return {
        rowKey: ch.id,
        channelId: ch.id,
        number: ch.number,
        name: ch.name,
        source: item === undefined ? (ch.sourceText ?? '') : itemName(item),
        model: brandModel(item),
        phantom: ch.phantom ?? needsPhantom(data, ch),
        patch: formal ?? ch.patch ?? '',
        patchInformal: formal === undefined && (ch.patch ?? '') !== '',
        subPatch: formalSub ?? ch.subPatch ?? '',
        subPatchInformal: formalSub === undefined && (ch.subPatch ?? '') !== '',
        group: ch.group ?? item?.group ?? '',
        stand: stand === undefined ? (ch.stand ?? '') : standText(stand, locale, data.meta.units),
        standInformal: stand === undefined && (ch.stand ?? '') !== '',
        insert: ch.insert ?? '',
        notes: ch.notes ?? '',
      };
    })
    .sort(byNumber);
}

export const INPUT_LIST_COLUMNS = fieldColumns<InputListRow>('input', [
  'number',
  'name',
  'source',
  'model',
  'phantom',
  'patch',
  'subPatch',
  'group',
  'stand',
  'insert',
  'notes',
]);
